import React, { useEffect, useRef } from 'react';

export type InteractiveDotBackgroundProps = {
  className?: string;
  /** Base dot color */
  color?: string;
  /** Dot color when cursor is nearby (blended by proximity) */
  accentColor?: string;
};

const MAX_DPR = 2;
const MAX_DOTS = 4200;
const BASE_SPACING = 28;
const MOUSE_RADIUS = 155;
const MOUSE_SMOOTH = 0.14;
const OFFSET_LERP = 0.11;
const IDLE_FREQ = 0.00085;
const IDLE_AMP = 2.2;
const IDLE_AMP_COARSE = 1.05;
const REPULSE = 26;
const NEAR_BOOST_RADIUS = 90;

function clampSpacing(w: number, h: number, spacing: number): number {
  let s = spacing;
  while (Math.ceil(w / s) * Math.ceil(h / s) > MAX_DOTS && s < 80) {
    s += 4;
  }
  return s;
}

function parseRgb(rgb: string): { r: number; g: number; b: number; a: number } {
  const m = rgb.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (m) {
    return {
      r: Number(m[1]),
      g: Number(m[2]),
      b: Number(m[3]),
      a: m[4] !== undefined ? Number(m[4]) : 1,
    };
  }
  return { r: 71, g: 85, b: 105, a: 0.3 };
}

/**
 * Full-viewport canvas dot field: subtle idle motion, pointer repel + proximity glow.
 * Uses coarse idle motion on touch-primary devices; interaction works for mouse, touch, and pen (Pointer Events).
 * Respects prefers-reduced-motion; container is pointer-events none (hits fall through) — listeners on window/container bubble.
 */
export const InteractiveDotBackground: React.FC<InteractiveDotBackgroundProps> = ({
  className = '',
  color = 'rgba(71, 85, 105, 0.32)',
  accentColor = 'rgba(59, 130, 246, 0.58)',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    const containerEl = containerRef.current;
    if (!canvasEl || !containerEl) return;
    const canvas = canvasEl;
    const container = containerEl;

    const ctxMaybe = canvas.getContext('2d', { alpha: true });
    if (!ctxMaybe) return;
    const c2d: CanvasRenderingContext2D = ctxMaybe;

    const baseRgb = parseRgb(color);
    const accentRgb = parseRgb(accentColor);

    const reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointerMq = window.matchMedia('(hover: hover) and (pointer: fine)');

    let reducedMotion = reducedMotionMq.matches;
    /** Stronger idle drift on mouse/desktop; calmer on touch-first viewports. */
    let finePointer = finePointerMq.matches;

    let raf = 0;
    let running = false;

    let cssW = 1;
    let cssH = 1;
    let dpr = 1;
    let spacing = BASE_SPACING;
    let cols = 0;
    let rows = 0;
    let count = 0;

    let gridBaseX = new Float32Array(0);
    let gridBaseY = new Float32Array(0);
    let gridOx = new Float32Array(0);
    let gridOy = new Float32Array(0);

    let mouseCssX = -1e6;
    let mouseCssY = -1e6;
    let smoothMx = -1e6;
    let smoothMy = -1e6;
    let mouseActive = false;
    /** Non-mouse pointers (touch/pen) currently down — tracked for interaction when fine-pointer MQ is false. */
    const activeTouchIds = new Set<number>();

    function syncPointerToContainer(clientX: number, clientY: number) {
      const rect = container.getBoundingClientRect();
      mouseCssX = clientX - rect.left;
      mouseCssY = clientY - rect.top;
    }

    function pointerInsideContainer(clientX: number, clientY: number): boolean {
      const rect = container.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    }

    function rebuildGrid() {
      const rect = container.getBoundingClientRect();
      cssW = Math.max(1, rect.width);
      cssH = Math.max(1, rect.height);
      dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      spacing = clampSpacing(cssW, cssH, BASE_SPACING);

      cols = Math.floor(cssW / spacing) + 3;
      rows = Math.floor(cssH / spacing) + 3;
      count = cols * rows;

      const marginX = (cssW - (cols - 1) * spacing) / 2;
      const marginY = (cssH - (rows - 1) * spacing) / 2;

      const nextBaseX = new Float32Array(count);
      const nextBaseY = new Float32Array(count);
      const nextOx = new Float32Array(count);
      const nextOy = new Float32Array(count);

      let i = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          nextBaseX[i] = marginX + c * spacing;
          nextBaseY[i] = marginY + r * spacing;
          nextOx[i] = 0;
          nextOy[i] = 0;
          i++;
        }
      }

      gridBaseX = nextBaseX;
      gridBaseY = nextBaseY;
      gridOx = nextOx;
      gridOy = nextOy;

      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function drawStatic() {
      c2d.clearRect(0, 0, cssW, cssH);
      const r = 1.35;
      c2d.fillStyle = color;
      for (let i = 0; i < count; i++) {
        const x = gridBaseX[i] ?? 0;
        const y = gridBaseY[i] ?? 0;
        c2d.beginPath();
        c2d.arc(x, y, r, 0, Math.PI * 2);
        c2d.fill();
      }
    }

    function frame(t: number) {
      if (!running) return;

      const time = t * IDLE_FREQ;
      const idleAmp = finePointer ? IDLE_AMP : IDLE_AMP_COARSE;

      if (!reducedMotion && mouseActive) {
        smoothMx += (mouseCssX - smoothMx) * MOUSE_SMOOTH;
        smoothMy += (mouseCssY - smoothMy) * MOUSE_SMOOTH;
      } else {
        smoothMx += (mouseCssX - smoothMx) * MOUSE_SMOOTH * 0.35;
        smoothMy += (mouseCssY - smoothMy) * MOUSE_SMOOTH * 0.35;
      }

      const mx = smoothMx;
      const my = smoothMy;
      const R = MOUSE_RADIUS;
      const R2 = R * R;

      c2d.clearRect(0, 0, cssW, cssH);

      for (let i = 0; i < count; i++) {
        const bx = gridBaseX[i] ?? 0;
        const by = gridBaseY[i] ?? 0;

        let tx = Math.sin(time + bx * 0.01) * idleAmp * 0.35;
        let ty = Math.cos(time * 0.9 + by * 0.012) * idleAmp * 0.35;

        let near = 0;
        let dispScale = 1;

        if (!reducedMotion && mouseActive) {
          const dx = bx - mx;
          const dy = by - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < R2 && d2 > 0.0001) {
            const d = Math.sqrt(d2);
            const u = d / R;
            const falloff = (1 - u) * (1 - u) * (1 - u);
            const inv = 1 / d;
            tx += dx * inv * REPULSE * falloff;
            ty += dy * inv * REPULSE * falloff;
            if (d < NEAR_BOOST_RADIUS) {
              near = (1 - d / NEAR_BOOST_RADIUS) * (1 - d / NEAR_BOOST_RADIUS);
            }
            dispScale = 1 + near * 0.55;
          }
        }

        const ox = gridOx[i] ?? 0;
        const oy = gridOy[i] ?? 0;
        gridOx[i] = ox + (tx - ox) * OFFSET_LERP;
        gridOy[i] = oy + (ty - oy) * OFFSET_LERP;

        const px = bx + (gridOx[i] ?? 0);
        const py = by + (gridOy[i] ?? 0);

        const br = baseRgb.r + (accentRgb.r - baseRgb.r) * near;
        const bgc = baseRgb.g + (accentRgb.g - baseRgb.g) * near;
        const bb = baseRgb.b + (accentRgb.b - baseRgb.b) * near;
        const ba = baseRgb.a + (accentRgb.a - baseRgb.a) * near * 0.85 + near * 0.12;

        const radius = (1.25 + near * 0.9) * dispScale;

        c2d.fillStyle = `rgba(${br | 0},${bgc | 0},${bb | 0},${Math.min(1, ba)})`;
        c2d.beginPath();
        c2d.arc(px, py, radius, 0, Math.PI * 2);
        c2d.fill();
      }

      raf = requestAnimationFrame(frame);
    }

    function startLoop() {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    }

    function stopLoop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    const onPointerMove = (e: PointerEvent) => {
      if (reducedMotion) return;
      syncPointerToContainer(e.clientX, e.clientY);
      if (e.pointerType === 'mouse') {
        mouseActive = pointerInsideContainer(e.clientX, e.clientY);
      } else if (activeTouchIds.has(e.pointerId)) {
        mouseActive = true;
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (reducedMotion) return;
      if (e.pointerType !== 'mouse') {
        activeTouchIds.add(e.pointerId);
        syncPointerToContainer(e.clientX, e.clientY);
        mouseActive = true;
      }
    };

    const onPointerUpOrCancel = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') {
        activeTouchIds.delete(e.pointerId);
        if (activeTouchIds.size === 0) {
          mouseActive = false;
        }
      }
    };

    const attachPointerListeners = () => {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerdown', onPointerDown, { passive: true });
      window.addEventListener('pointerup', onPointerUpOrCancel, { passive: true });
      window.addEventListener('pointercancel', onPointerUpOrCancel, { passive: true });
    };

    const detachPointerListeners = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUpOrCancel);
      window.removeEventListener('pointercancel', onPointerUpOrCancel);
    };

    function applyMotionMode() {
      reducedMotion = reducedMotionMq.matches;
      finePointer = finePointerMq.matches;

      stopLoop();
      detachPointerListeners();
      activeTouchIds.clear();

      rebuildGrid();

      if (reducedMotion) {
        drawStatic();
        return;
      }

      attachPointerListeners();
      if (!document.hidden) {
        startLoop();
      }
    }

    const onReducedMotionChange = () => applyMotionMode();
    const onFinePointerChange = () => applyMotionMode();

    reducedMotionMq.addEventListener('change', onReducedMotionChange);
    finePointerMq.addEventListener('change', onFinePointerChange);

    const ro = new ResizeObserver(() => {
      rebuildGrid();
      if (reducedMotionMq.matches) {
        stopLoop();
        drawStatic();
      } else if (!document.hidden && !running) {
        detachPointerListeners();
        attachPointerListeners();
        startLoop();
      }
    });

    const onVisibility = () => {
      if (document.hidden) {
        stopLoop();
      } else if (!reducedMotionMq.matches) {
        startLoop();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    ro.observe(container);
    applyMotionMode();

    return () => {
      reducedMotionMq.removeEventListener('change', onReducedMotionChange);
      finePointerMq.removeEventListener('change', onFinePointerChange);
      document.removeEventListener('visibilitychange', onVisibility);
      stopLoop();
      ro.disconnect();
      detachPointerListeners();
      activeTouchIds.clear();
    };
  }, [color, accentColor]);

  return (
    <div
      ref={containerRef}
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden={true}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default InteractiveDotBackground;
